import { Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, tap } from 'rxjs';
import { ApiService } from './api.service';
import { User } from '../models/user.model';

interface TokenResponse {
  access_token: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSignal = signal<User | null>(null);
  currentUser = this.currentUserSignal.asReadonly();

  constructor(
    private api: ApiService,
    private router: Router
  ) {
    this.loadUserFromStorage();
  }

  login(email: string, password: string): Observable<TokenResponse> {
    // Utilise l'endpoint sécurisé de LaBase Admin qui définit les profils requis côté serveur
    return this.api.post<TokenResponse>('/labase/admin/auth/login', { email, password }).pipe(
      tap(response => {
        // Décoder le JWT pour extraire les données utilisateur
        const user = this.decodeToken(response.access_token);
        this.setSession(response.access_token, user);
      })
    );
  }

  private decodeToken(token: string): User {
    const payload = JSON.parse(atob(token.split('.')[1]));

    return {
      id: payload.id,
      email: payload.email,
      nom: payload.nom,
      prenom: payload.prenom,
      tel: payload.tel,
      adresse: payload.adresse,
      avatar: payload.avatar,
      profils: payload.profils || [],
      statutcomptId: payload.statutcompt_id ?? 1,
      is_activated: payload.is_activated ?? 1,
      restreint: payload.restreint,
      no_sms: payload.no_sms,
      recevoir_info: payload.recevoir_info,
      app_data: payload.app_data,
      matricule: payload.matricule,
      date_naissance: payload.date_naissance,
      createdAt: payload.createdAt,
      updatedAt: payload.updatedAt
    };
  }

  private setSession(token: string, user: User): void {
    localStorage.setItem('access_token', token);
    localStorage.setItem('current_user', JSON.stringify(user));
    this.currentUserSignal.set(user);
  }

  private loadUserFromStorage(): void {
    const token = localStorage.getItem('access_token');
    const userJson = localStorage.getItem('current_user');

    if (token && userJson) {
      try {
        const user = JSON.parse(userJson) as User;
        this.currentUserSignal.set(user);
      } catch (e) {
        this.logout();
      }
    }
  }

  getToken(): string | null {
    return localStorage.getItem('access_token');
  }

  logout(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('current_user');
    this.currentUserSignal.set(null);
    this.router.navigate(['/login']);
  }

  isAuthenticated(): boolean {
    const token = localStorage.getItem('access_token');
    return !!token;
  }

  hasAdminProfile(): boolean {
    const user = this.currentUserSignal();
    if (!user || !user.profils) return false;

    return user.profils.some(p => p.code === 'magasin' || p.code === 'super_admin');
  }
}
